import React, { useState } from 'react';
import { Card, Col, ListGroup, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { RequestedAbsenceQuery } from '../../__generated__/graphql';
import AbsenceAmount from '../Absences/components/AbsenceAmount';
import AbsenceDateTime from '../Absences/components/AbsenceDateTime';
import { mapDocumentFile } from '../Absences/mappings';
import { LoomVideoPreview, mapLoomVideo } from '../../components/LoomVideo';
import {
  ExtraFieldsSupportedObject,
  ExtraFieldsViewArea,
} from '../../components/ExtraFields';
import { ExtraFieldsValueToRender } from '../../components/ExtraFields/ExtraFieldsViewArea/ExtraFieldsViewAreaContent';
import { FieldValue } from '../../components/EmployeeData/FieldValue';
import { ExtraFieldsFormId } from '../../components/ExtraFields/types';
import StringToHtml from '../../../../components/StringToHtml';
import MultiDayAbsenceBtn from '../Absences/components/MultiDayAbsenceDetails/MultiDayAbsenceBtn';
import MultiDayAbsenceDetails from '../Absences/components/MultiDayAbsenceDetails';
import { isMultiDayAndHourly } from '../Absences/components/MultiDayAbsenceDetails/logic';
import { useExtraAbsenceExtraFieldsContextParams } from '../../components/RequestAbsenceSidebar/useExtraAbsenceExtraFieldsContextParams';
import ApprovalPath from '../ApprovalRequests/ApprovalPath';
import FlairCard from '../../../../atomic/templates/FlairCard/FlairCard';
import AbsenceStatus from '../Absences/components/AbsenceStatus';
import FormattedDate from '../../../../components/date/FormattedDate';

type Props = {
  absence: RequestedAbsenceQuery['absence'];
  hideApprovalPath?: boolean;
  hideAttachment?: boolean;
};

const Detail: React.FC<React.PropsWithChildren<Props>> = ({
  absence,
  hideApprovalPath = false,
  hideAttachment = false,
}) => {
  const contextParams = useExtraAbsenceExtraFieldsContextParams({
    employeeAbsenceCategoryId: absence.employeeAbsenceCategory?.Id ?? '',
    categoryName: absence.flair__Category_Name__c,
  });

  const { t } = useTranslation();
  const [selectedAbsenceId, setSelectedAbsenceId] = useState<string>('');

  if (
    !absence.employeeAbsenceCategory ||
    !absence.approvalRequests ||
    !absence.CreatedDate
  ) {
    // not allow to see sensitive data
    return null;
  }

  const availableAmount =
    absence.employeeAbsenceCategory.allowancesSummary.availableAmount;
  const rejectedRequest = absence.approvalRequests.find(
    (request) => request.flair__Approval_Status__c === 'REJECTED',
  );
  const hasRejectionComment =
    rejectedRequest && rejectedRequest.flair__Approver_Comment__c;

  const attachments =
    absence.ContentDocumentLinks__r?.map(mapDocumentFile) ?? [];
  const attachment = attachments.length > 0 ? attachments[0] : null;

  const loomVideo = mapLoomVideo(absence.loomVideo);

  const handleCloseSidebar = () => {
    setSelectedAbsenceId('');
  };

  const renderItems = (items: ExtraFieldsValueToRender[]) => {
    return (
      <>
        {items.map((item, index) => (
          <ListGroup.Item key={item.fieldApiName}>
            <Row className="align-items-center">
              <Col>
                <b>{item.label}</b>
              </Col>
              <Col className="col-auto">
                {item.isRichTextArea && item.inputValue ? (
                  <StringToHtml text={item.inputValue} />
                ) : (
                  <FieldValue
                    type={item.fieldType}
                    options={[]}
                    value={item.inputValue}
                  />
                )}
              </Col>
            </Row>
          </ListGroup.Item>
        ))}
      </>
    );
  };

  return (
    <div className="d-flex flex-row gap-4 align-items-start">
      <Card className="flex-grow-1">
        <Card.Body>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <Row className="align-items-center">
                <Col>
                  <b>{t('absenceRequest.fields.absenceStatus')}</b>
                </Col>
                <Col className="col-auto">
                  <AbsenceStatus status={absence.flair__Approval_Status__c} />
                </Col>
              </Row>
            </ListGroup.Item>
            <ListGroup.Item>
              <Row className="align-items-center">
                <Col>
                  <b>{t('absenceRequest.fields.employee')}</b>
                </Col>
                <Col className="col-auto">{absence.employee.Name}</Col>
              </Row>
            </ListGroup.Item>
            <ListGroup.Item>
              <Row className="align-items-center">
                <Col>
                  <b>{t('absenceRequest.fields.from')}</b>
                </Col>
                <Col className="col-auto">
                  <AbsenceDateTime
                    date={absence.flair__Start_Date__c}
                    time={absence.flair__Start_Time__c}
                    type={absence.flair__Type__c}
                    halfDay={absence.flair__Start_Date_Half_Day__c}
                  />
                </Col>
              </Row>
            </ListGroup.Item>
            <ListGroup.Item>
              <Row className="align-items-center">
                <Col>
                  <b>{t('absenceRequest.fields.to')}</b>
                </Col>
                <Col className="col-auto">
                  <AbsenceDateTime
                    date={absence.flair__End_Date__c}
                    time={absence.flair__End_Time__c}
                    type={absence.flair__Type__c}
                    halfDay={absence.flair__End_Date_Half_Day__c}
                  />
                </Col>
              </Row>
            </ListGroup.Item>
            <ListGroup.Item>
              <Row className="align-items-center">
                <Col>
                  <b>{t('absenceRequest.fields.amount')}</b>
                </Col>
                <Col className="col-auto">
                  <div className="d-flex align-items-center gap-2">
                    <AbsenceAmount
                      type={absence.flair__Type__c}
                      amount={absence.flair__Working_Amount__c}
                      variant="work"
                    />
                    {isMultiDayAndHourly(absence) && (
                      <>
                        &nbsp;&#xB7;&nbsp;
                        <MultiDayAbsenceBtn
                          absenceId={absence.Id}
                          onClick={setSelectedAbsenceId}
                        />
                      </>
                    )}
                  </div>
                </Col>
              </Row>
            </ListGroup.Item>
            <ListGroup.Item>
              <Row className="align-items-center">
                <Col>
                  <b>{t('absenceRequest.fields.category')}</b>
                </Col>
                <Col className="col-auto">
                  {absence.flair__Category_Name__c}
                </Col>
              </Row>
            </ListGroup.Item>
            <ListGroup.Item>
              <Row className="align-items-center">
                <Col>
                  <b>{t('absenceRequest.fields.remainingAmount')}</b>
                </Col>
                <Col className="col-auto">
                  <AbsenceAmount
                    type={absence.flair__Type__c}
                    amount={availableAmount}
                    variant="work"
                  />
                </Col>
              </Row>
            </ListGroup.Item>
            <ListGroup.Item>
              <Row className="align-items-center">
                <Col>
                  <b>{t('absenceRequest.fields.requestedAt')}</b>
                </Col>
                <Col className="col-auto">
                  <FormattedDate day={absence.CreatedDate} format="long" />
                </Col>
              </Row>
            </ListGroup.Item>
            <ListGroup.Item>
              <Row className="align-items-center">
                <Col>
                  <b>{t('absenceRequest.fields.comment')}</b>
                </Col>
                <Col className="col-auto">
                  {absence.flair__Comment__c ? (
                    <q>{absence.flair__Comment__c}</q>
                  ) : (
                    <span className="text-muted">
                      {t('general.notAvailable')}
                    </span>
                  )}
                </Col>
              </Row>
            </ListGroup.Item>
            <ListGroup.Item>
              <Row className="align-items-center">
                <Col>
                  <b>{t('absenceRequest.fields.reviewerComment')}</b>
                </Col>
                <Col className="col-auto">
                  {hasRejectionComment ? (
                    <q>{rejectedRequest.flair__Approver_Comment__c}</q>
                  ) : (
                    <span className="text-muted">
                      {t('general.notAvailable')}
                    </span>
                  )}
                </Col>
              </Row>
            </ListGroup.Item>
            {!hideAttachment && attachment && (
              <ListGroup.Item>
                <Row className="align-items-center">
                  <Col>
                    <b>{t('absenceRequest.fields.attachment')}</b>
                  </Col>
                  <Col className="col-auto">
                    <a
                      rel="noreferrer"
                      target="_blank"
                      href={attachment.downloadPublicUrl}>
                      {attachment.fileName}
                    </a>
                  </Col>
                </Row>
              </ListGroup.Item>
            )}
            {loomVideo && (
              <ListGroup.Item>
                <Row className="align-items-center">
                  <Col>
                    <b>{t('absenceRequest.fields.loom')}</b>
                  </Col>
                  <Col className="col-auto">
                    <LoomVideoPreview
                      loomVideo={loomVideo}
                      previewWidth={153}
                      previewHeight={100}
                    />
                  </Col>
                </Row>
              </ListGroup.Item>
            )}
            <ExtraFieldsViewArea
              objectApiName={ExtraFieldsSupportedObject.Absence}
              formId={ExtraFieldsFormId.AbsencesManagerDetails}
              recordId={absence.Id}
              renderItems={renderItems}
              contextParams={contextParams}
            />
          </ListGroup>
        </Card.Body>
      </Card>
      {!hideApprovalPath && (
        <FlairCard
          cardTitle={t('approvalRequest.approvalPath.title')}
          cardContainerClassName="rounded-3">
          <ApprovalPath relatedRecordId={absence.Id} />
        </FlairCard>
      )}
      {selectedAbsenceId && (
        <MultiDayAbsenceDetails
          absenceId={selectedAbsenceId}
          show={true}
          onClose={handleCloseSidebar}
        />
      )}
    </div>
  );
};

export default Detail;
